import React from 'react';
import { useModelGroupsQuery } from '../../../AdviserSettings/Page/Models/api/useModelGroupsQuery';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Loading } from '../../../CommonComponents/OPL/Loading/Loading';
import {
  MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES,
  ManualAccountsAssetAllocationDTO,
} from '../../../ManualAccountsAssetAllocation/types';
import { AssetAllocationDTO } from '../../../Profile/Accounts/api/useDefaultAssetAllocationQuery';
import { Tab, TabList, TabPanel, Tabs } from '../../../CommonComponents/OPL/Tab';
import { AssetAllocationTableFormContent } from '../../../Profile/Accounts/Features/AccountPreferences/components/AccountForm/components/AssetAllocationTable/AssetAllocationTable';
import { HtmlTable } from '../../../CommonComponents/OPL/HtmlTable/HtmlTable';
import { formatPercentage } from '../../../CommonUtils/formatNumber';
import { HelperText } from '../../../CommonComponents/OPL/HelperText/HelperText';

interface Props {
  currentAllocation: ManualAccountsAssetAllocationDTO;
  setCurrentAllocation: (allocation: ManualAccountsAssetAllocationDTO) => void;
  clientId?: number;
}

const getTotal = (assetAllocation: AssetAllocationDTO[] = []) => {
  return Math.round(assetAllocation.reduce((acc, allocation) => acc + (allocation.y || 0), 0));
};

export const CMA = ({ currentAllocation, clientId, setCurrentAllocation }: Props) => {
  const { data: modelGroups, isLoading } = useModelGroupsQuery(clientId ? { clientId } : undefined);

  if (isLoading) {
    return (
      <Box p="40">
        <Loading />
      </Box>
    );
  }

  if (!modelGroups) {
    return null;
  }

  return (
    <Tabs>
      <TabList mb="8">
        <Tab>Taxable</Tab>
        <Tab>Tax-Deferred</Tab>
        <Tab>Tax-Free</Tab>
        <Tab>Other</Tab>
      </TabList>

      {MANUAL_ACCOUNTS_ASSET_ALLOCATION_TAX_STATUSES.map(taxStatusId => {
        const allocationDetails = modelGroups.options.defaultAssetAllocation.map(a => {
          if (!currentAllocation.details) {
            return { ...a, y: a.y * 100 };
          }

          const taxStatusAllocation = currentAllocation.details[taxStatusId];

          if (!Array.isArray(taxStatusAllocation)) {
            return { ...a, y: a.y * 100 };
          }

          return {
            ...a,
            y: taxStatusAllocation.find(aa => aa.id === a.id)?.y ?? a.y * 100,
          };
        });

        const taxStatusAllocation = currentAllocation.details?.[taxStatusId];
        const total = Array.isArray(taxStatusAllocation) ? getTotal(taxStatusAllocation) : 0;

        const isInvalid = total !== 100;

        return (
          <TabPanel key={taxStatusId}>
            <AssetAllocationTableFormContent
              key={taxStatusId}
              assetAllocations={allocationDetails}
              onChange={allocations => {
                const currentDetails = currentAllocation.details || {
                  TaxableInvestment: [],
                  TaxDeferred: [],
                  TaxFree: [],
                  Other: [],
                };

                setCurrentAllocation({
                  ...currentAllocation,
                  details: {
                    ...currentDetails,
                    [taxStatusId]: allocations,
                  },
                });
              }}
            />
            <Box css="margin: 0 -24px;">
              <HtmlTable variant="embedded" css="max-height: 265px; overflow-y: auto">
                <table>
                  <thead>
                    <tr>
                      <th css="position: sticky; top: 0; z-index: 10">
                        <Text variant="smallCaps" textAlign="left">
                          Total
                        </Text>
                      </th>

                      <th css="position: sticky; top: 0; z-index: 10">
                        <Text variant="smallCaps" textAlign="right">
                          {formatPercentage(total / 100)}
                        </Text>
                      </th>
                    </tr>
                  </thead>
                </table>
              </HtmlTable>
            </Box>

            {isInvalid && (
              <Box display="flex" justifyContent="flex-end">
                <HelperText variant="error" mt="4">
                  The total must add up to 100%
                </HelperText>
              </Box>
            )}
          </TabPanel>
        );
      })}
    </Tabs>
  );
};
